/**
 * The console's one dropdown control — the pill with a chevron that the Item
 * list's header row is built from.
 *
 * ## Why it exists
 *
 * The design draws five of these side by side. Four of them were plain
 * `<div>`s with a decorative chevron and no handler (spec §6.3 shipped them
 * "as drawn"), and one — `Group` — was a button that cycled. Wiring `Sort`
 * made the row actively misleading rather than merely incomplete: five
 * identical-looking pills, two of which responded to a click. A reader has no
 * way to tell which is which until they try.
 *
 * So: one component. Anything that renders as a pill with a chevron IS a
 * menu, and there is nowhere left to put a control that only looks like one.
 *
 * ## Selection changes the URL, never local state
 *
 * Every option carries the href it navigates to, and choosing one navigates.
 * That keeps filter and sort state in the same place as `?workspace=`,
 * `?view=` and `?groupBy=` — so a reload, a bookmark and a pasted link all
 * resolve the same list, and the SERVER decides what the list contains. A
 * control that filtered locally would disagree with the header's own counts
 * the moment it was used.
 */
import { useEffect, useRef, useState } from "react";
import { C, FONT_MONO } from "../tokens";
import { META } from "./chrome";

export interface MenuOption<T extends string> {
  value: T;
  label: string;
  /** The secondary line. Not decoration — `Attention` is a computed score,
   *  and a reader who cannot see what it weighs has no reason to trust the
   *  order it produces. Omit where the label is self-evident. */
  hint?: string;
  /** How many rows this option would leave on screen. `filter` prints it at
   *  the right of the row so the choice is informed before it is made; `chip`
   *  ignores it. */
  meta?: string;
  /** A status colour, drawn as an 8px dot. Same rule as the picker's: a menu
   *  where some rows have a dot and some do not reads as a fault, so a caller
   *  that supplies one supplies them for every option. */
  dot?: string;
  icon?: React.ReactNode;
  detail?: string;
  /** Draw a hairline BELOW this option — the design's way of separating "Any"
   *  from the values it stands in for. */
  divider?: boolean;
}

export function MenuControl<T extends string>({
  testId,
  label,
  value,
  options,
  onSelect,
  locked,
  align = "left",
  variant = "chip",
  meta,
  dot,
  active = false,
  icon,
  valueIcon,
}: {
  /** Prefix for the control's own test id and each option's. */
  testId: string;
  label: string;
  /** The CURRENT value's display text. */
  value: string;
  options: MenuOption<T>[];
  onSelect: (next: T) => void;
  /** A saved view has already fixed this dimension: render the padlock and
   *  refuse to open. Still a `<button>`, so the row's shape does not shift. */
  locked?: boolean;
  align?: "left" | "right";
  /** `chip` — the 32px pill (default). `filter` — the ISSUES screen's 48px
   *  card. See the file header. */
  variant?: "chip" | "filter";
  /** `filter` only: the count beside the value. */
  meta?: string;
  /** `filter` only: an 8px status dot before the value. */
  dot?: string;
  /** `filter` only: whether this dimension is actually narrowing anything.
   *  An untouched filter is drawn a step quieter, so a row of five reads as
   *  "one of these is doing something" at a glance rather than as five equal
   *  claims. */
  active?: boolean;
  /** A glyph before the label. `Sort` wears one so it reads as a sort rather
   *  than as a fourth filter — it is the one control in this row that changes
   *  ORDER, not membership. */
  icon?: React.ReactNode;
  /** Mark or avatar beside the selected value. */
  valueIcon?: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLSpanElement>(null);

  // Click-away and Escape. A menu that can only be dismissed by choosing
  // something traps a reader who opened it to see what the options were.
  useEffect(() => {
    if (!open) return;
    const onDown = (event: MouseEvent) => {
      if (!wrap.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const isFilter = variant === "filter";

  return (
    <span
      ref={wrap}
      style={
        isFilter
          ? // A filter shares the row's width rather than hugging its label:
            // five cards of five different widths made the row read as debris.
            // 240 is the floor at which the label, the value and the count all
            // still fit; 360 the ceiling past which one filter looks like a
            // search box.
            { position: "relative", flex: "1 1 240px", maxWidth: 360, minWidth: 0 }
          : { display: "inline-flex", alignItems: "center", position: "relative" }
      }
    >
      <button
        onClick={() => !locked && setOpen((value) => !value)}
        data-testid={`${testId}-control`}
        aria-haspopup={locked ? undefined : "menu"}
        aria-expanded={locked ? undefined : open}
        aria-disabled={locked || undefined}
        data-active={isFilter ? (active ? "true" : "false") : undefined}
        style={{
          display: "flex",
          alignItems: "center",
          width: isFilter ? "100%" : undefined,
          boxSizing: "border-box",
          gap: isFilter ? 10 : 7,
          height: isFilter ? 48 : 32,
          padding: isFilter ? "0 12px 0 14px" : "0 12px",
          borderRadius: isFilter ? 14 : 9,
          background: locked ? C.faint : C.surface,
          // A filter that is doing nothing wears the quiet edge; one that is
          // narrowing the list wears the same edge every other control does.
          border: `1px solid ${isFilter && !active ? C.hair : C.line2}`,
          ...(isFilter
            ? { boxShadow: `0 1px 2px ${C.softShadow}, 0 4px 12px ${C.softShadow}` }
            : {}),
          cursor: locked ? "default" : "pointer",
          font: "inherit",
          color: "inherit",
          textAlign: "left",
        }}
      >
        {icon}
        <span
          style={
            isFilter
              ? {
                  ...META,
                  fontSize: 10.93,
                  fontWeight: 700,
                  letterSpacing: "0.95px",
                  color: C.filterLabel,
                  whiteSpace: "nowrap",
                }
              : { ...META, fontSize: 10.35, letterSpacing: "0.7px", color: C.sub2 }
          }
        >
          {label}
        </span>
        {isFilter && (
          <span
            aria-hidden
            style={{ width: 1, height: 18, background: C.line, flex: "none" }}
          />
        )}
        {isFilter && valueIcon}
        {isFilter && dot && (
          <span
            aria-hidden
            style={{ width: 8, height: 8, borderRadius: 4, background: dot, flex: "none" }}
          />
        )}
        <span
          style={
            isFilter
              ? {
                  flex: 1,
                  minWidth: 0,
                  fontSize: 17.25,
                  fontWeight: 600,
                  letterSpacing: "-0.15px",
                  color: active ? C.ink : C.sub,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }
              : { fontSize: 14.37, fontWeight: 600, color: C.ink }
          }
        >
          {value}
        </span>
        {isFilter && meta && (
          <span
            style={{
              flex: "none",
              fontFamily: FONT_MONO,
              fontSize: 12.65,
              color: C.propLabel,
              whiteSpace: "nowrap",
            }}
          >
            {meta}
          </span>
        )}
        {locked ? (
          <svg width={isFilter ? 12 : 9} height={isFilter ? 12 : 9} viewBox="0 0 16 16" fill="none">
            <rect x="3" y="7" width="10" height="7" rx="1.6" stroke={C.sub2} strokeWidth="1.7" />
            <path d="M5.5 7V5a2.5 2.5 0 015 0v2" stroke={C.sub2} strokeWidth="1.7" />
          </svg>
        ) : isFilter ? (
          // 14px, and it TURNS. The design rotates the same glyph rather than
          // swapping it, so the control says "this is the thing that is open".
          <span
            aria-hidden
            style={{
              display: "inline-flex",
              flex: "none",
              transform: open ? "rotate(180deg)" : "none",
              transition: "transform 120ms ease",
              color: C.propLabel,
            }}
          >
            <svg width="14" height="9" viewBox="0 0 14 9" fill="none">
              <path
                d="M1.5 1.5l5.5 5.5 5.5-5.5"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
        ) : (
          <svg width="8" height="5" viewBox="0 0 10 6" fill="none">
            <path
              d="M1 1l4 4 4-4"
              stroke={C.sub2}
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </button>
      {open && !locked && (
        <div
          data-testid={`${testId}-menu`}
          role="menu"
          style={{
            position: "absolute",
            // 56 clears the 48px card and leaves the 8px the design draws.
            top: isFilter ? 56 : 36,
            ...(isFilter ? { left: 0, width: "100%", minWidth: 280 } : { [align]: 0, minWidth: 210 }),
            zIndex: isFilter ? 40 : 50,
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
            gap: isFilter ? 2 : 0,
            maxHeight: "min(420px, 60vh)",
            overflowY: "auto",
            padding: isFilter ? 6 : 5,
            borderRadius: isFilter ? 14 : 12,
            background: C.surface,
            border: `1px solid ${isFilter ? C.popLine : C.line2}`,
            boxShadow: isFilter
              ? `0 1px 2px ${C.hairRow}, 0 14px 36px ${C.menuShadow}`
              : `0 10px 28px ${C.line}`,
          }}
        >
          {options.map((option) => {
            const chosen = option.label === value;
            return (
              <span key={option.value} style={{ display: "contents" }}>
                <button
                  role="menuitemradio"
                  aria-checked={chosen}
                  data-testid={`${testId}-option-${option.value}`}
                  onClick={() => {
                    setOpen(false);
                    onSelect(option.value);
                  }}
                  style={
                    isFilter
                      ? {
                          display: "flex",
                          alignItems: "center",
                          gap: 10,
                          width: "100%",
                          boxSizing: "border-box",
                          minHeight: option.detail ? 56 : 40,
                          flexShrink: 0,
                          padding: "0 10px",
                          borderRadius: 9,
                          border: "none",
                          textAlign: "left",
                          cursor: "pointer",
                          font: "inherit",
                          color: "inherit",
                          background: chosen ? C.railMenuOn : "transparent",
                        }
                      : {
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "flex-start",
                          gap: 1,
                          width: "100%",
                          padding: "7px 9px",
                          borderRadius: 8,
                          border: "none",
                          textAlign: "left",
                          cursor: "pointer",
                          font: "inherit",
                          color: "inherit",
                          background: chosen ? C.line : "transparent",
                        }
                  }
                >
                  {isFilter ? (
                    <>
                      {option.icon}
                      {option.dot && (
                        <span
                          aria-hidden
                          style={{
                            width: 8,
                            height: 8,
                            borderRadius: 4,
                            background: option.dot,
                            flex: "none",
                          }}
                        />
                      )}
                      <span
                        style={{
                          flex: 1,
                          minWidth: 0,
                          fontSize: 16.1,
                          fontWeight: chosen ? 600 : 500,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {option.label}
                        {option.detail && <span style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", fontSize: 12, fontWeight: 400, color: C.sub2 }}>{option.detail}</span>}
                      </span>
                      {option.meta && (
                        <span
                          style={{
                            fontFamily: FONT_MONO,
                            fontSize: 12.08,
                            color: C.propLabel,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {option.meta}
                        </span>
                      )}
                      {chosen && (
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
                          <path
                            d="M2.5 7.5l3 3 6-7"
                            stroke={C.railScope}
                            strokeWidth="1.7"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                    </>
                  ) : (
                    <>
                      <span style={{ fontSize: 14.37, fontWeight: 600 }}>{option.label}</span>
                      {option.hint && (
                        <span style={{ ...META, fontSize: 10.35, fontWeight: 400, color: C.sub2 }}>
                          {option.hint}
                        </span>
                      )}
                    </>
                  )}
                </button>
                {isFilter && option.divider && (
                  <span
                    aria-hidden
                    style={{ height: 1, background: C.railEdge, margin: "4px 6px" }}
                  />
                )}
              </span>
            );
          })}
        </div>
      )}
    </span>
  );
}
